/**
 * ZeroBounce on an ORDINARY email input — every field an originator authored with
 * `validate: 'email'`, wherever it sits in the workflow, checked when the person
 * filling it in tabs away.
 *
 * Pure decision logic, deliberately split out of the renderers for the same
 * reason {@link ./esignEmailGate} is: the rule is the thing that needs a test,
 * and keeping it free of `t()` and React lets it be tested as a table of
 * (status, in-flight) → verdict rather than through a render with a mocked API
 * client.
 *
 * <h3>Why this bar is not the e-signature step's bar</h3>
 * `esignEmailGate.ts` hard-blocks an advisor on `Unknown`, which is every
 * upstream failure mode wearing one name — ZeroBounce unreachable, key missing
 * from Secrets Manager, a 429 from our own rate limiter, a 4xx from a bad auth
 * policy. That is the right call THERE: the step is about to hand a signing
 * package to OneSpan for delivery, it is one step, and an advisor stuck on it
 * has an obvious way out (leave and re-enter, which retries).
 *
 * Here the blast radius is the whole product. These fields are scattered through
 * every step of every workflow, so blocking on `Unknown` would mean one expired
 * key makes every form in the platform unfillable, with no step to leave and
 * re-enter. So `Unknown` PASSES: a verdict we could not obtain is not evidence
 * against the address. Only `Invalid` — ZeroBounce actually saying the mailbox
 * does not exist — stops anyone.
 *
 * The warn statuses (`CatchAll`, `SpamTrap`, `DoNotMail`) pass here for the same
 * reason they pass there, confirmed by BMO 2026-10-06: they are judgements about
 * the mailbox's character, not its deliverability. They carry no UI at all on an
 * ordinary field — an amber glyph on a signatory roster is information, the same
 * glyph on a form field someone is halfway through is noise.
 */

import type { EmailVerifyResult } from '@/api/workflows';

import { runValidate } from './fieldValidation';

/**
 * The cache key for an address: trimmed and lowercased.
 *
 * One definition, used by the store, both renderers and the Next gate, so the
 * four can never disagree about which entry a value maps to. A mismatch here
 * would not throw — it would silently look up a key nobody wrote, read as "no
 * verdict yet", and pass. `OneSpanESignStep` inlines the same two calls for its
 * own batch; it now calls this instead.
 */
export function normalizeEmail(raw: string): string {
  return raw.trim().toLowerCase();
}

/**
 * Is this value worth a metered ZeroBounce call?
 *
 * Only once the local RFC-5322 check in {@link runValidate} has already passed.
 * Syntax is the free pre-gate — `jane@bmo` and `jane..doe@bmo.com` are rejected
 * on the spot with their own message, and spending a credit to be told the same
 * thing would be paying for an answer we already have. The BE applies its own
 * looser version of this before forwarding upstream; this is the half that also
 * saves the round-trip.
 */
export function isVerifiable(raw: string): boolean {
  const value = (raw ?? '').trim();
  if (value === '') return false;
  return runValidate('email', value) === null;
}

/**
 * What a field should show, and whether Next may proceed past it.
 *
 * - `'invalid'` — ZeroBounce says the mailbox is not deliverable. Red text, Next held.
 * - `'checking'` — a request for this address is outstanding. Next held for the
 *   handful of hundred milliseconds it takes, so a fast clicker cannot tab out of
 *   the last email field and hit Next in the gap before the answer lands.
 * - `null` — nothing to say. Covers a clean `Valid`, every warn status, an
 *   `Unknown`, and the case where no request was ever made (a blank or
 *   syntactically broken field, or the capability being switched off entirely for
 *   the public runtime).
 *
 * Note the asymmetry with `checking`: a request that has not STARTED is not a
 * reason to hold Next. Only an outstanding one is. Otherwise a field whose value
 * never qualified for a lookup would block forever.
 */
export type EmailFieldVerdict = 'invalid' | 'checking' | null;

export function emailFieldVerdict(
  result: EmailVerifyResult | undefined,
  inFlight: boolean,
): EmailFieldVerdict {
  // In-flight wins over a stale result: the advisor edited the address and the
  // answer for the NEW one has not arrived, so the old verdict is about a value
  // that is no longer on screen.
  if (inFlight) return 'checking';
  return result?.status === 'Invalid' ? 'invalid' : null;
}

/**
 * The verdict for one raw field value — the whole rule in one call, for callers
 * that hold a value rather than a looked-up result.
 *
 * A value that was never eligible for a lookup ({@link isVerifiable} false) is
 * `null` without consulting the store at all: `required` owns a blank field and
 * the syntax rule owns a malformed one, and neither wants a second opinion.
 */
export function emailValueVerdict(
  raw: string,
  lookup: EmailVerifyLookup | null | undefined,
): EmailFieldVerdict {
  if (!lookup || !isVerifiable(raw)) return null;
  const { result, inFlight } = lookup(normalizeEmail(raw));
  return emailFieldVerdict(result, inFlight);
}

/**
 * What the GLYPH inside the input shows — a different question from
 * {@link emailFieldVerdict}, and deliberately a separate function.
 *
 * The verdict answers "may Next proceed", and {@link failsFormatRule} in FormShell holds the
 * button on anything non-null. So `valid` cannot be folded into it: a verified address would
 * then read as a blocked one and Next would never enable again. Two functions, two questions,
 * one store.
 *
 * - `'checking'` — spinner. A request for this address is outstanding.
 * - `'valid'` — green check. ZeroBounce confirmed the mailbox.
 * - `'invalid'` — red exclamation, beside the red text `handleBlur` already writes.
 * - `null` — no glyph. The warn statuses (`CatchAll`, `SpamTrap`, `DoNotMail`) and `Unknown`
 *   land here, along with an address nobody has asked about yet.
 *
 * <h3>Why warn and Unknown stay silent on a field</h3>
 * Both PASS this gate (see the header above), and a glyph that stops nobody is a question the
 * person filling the form cannot act on — on a signatory roster an amber mark is information
 * about a package about to be delivered, on a field someone is halfway through it is noise.
 * `Unknown` is the sharper case: the e-signature step paints it red because there it kills
 * Next, and copying that here would claim a block that does not exist.
 */
export type EmailFieldIndicator = 'checking' | 'valid' | 'invalid' | null;

export function emailFieldIndicator(
  result: EmailVerifyResult | undefined,
  inFlight: boolean,
): EmailFieldIndicator {
  // Same precedence as the verdict: an outstanding request outranks a settled result, which is
  // about an address no longer on screen.
  if (inFlight) return 'checking';
  if (result?.status === 'Invalid') return 'invalid';
  if (result?.status === 'Valid') return 'valid';
  return null;
}

/**
 * The glyph for one raw field value. Null for a value that was never eligible for a lookup, so
 * editing a verified address to a blank or malformed one drops the check mark rather than
 * leaving it to vouch for text it never saw.
 */
export function emailValueIndicator(
  raw: string,
  lookup: EmailVerifyLookup | null | undefined,
): EmailFieldIndicator {
  if (!lookup || !isVerifiable(raw)) return null;
  const { result, inFlight } = lookup(normalizeEmail(raw));
  return emailFieldIndicator(result, inFlight);
}

/** What the store exposes for reading one address's state. See `useEmailVerification`. */
export type EmailVerifyLookup = (normalizedEmail: string) => {
  result: EmailVerifyResult | undefined;
  inFlight: boolean;
};
